import React, { useEffect, useMemo, useState } from 'react';
import { API_URL } from '../../../config';
import type {
  TimedRunInvalidReason,
  TimedRunLeaderboardPage,
  TimedRunPublicEntry,
  TimedRunRulesetHistoryResponse,
} from '../../../shared/types/TimedRun';
import {
  formatTimedRunElapsed,
  timedRunElapsedMs,
  useTimedRunClientState,
} from '../../services/TimedRunClientService';
import { FONTS, formatCallingName } from '../theme';
import { useHudTomeVibe } from '../HUD/useHudTomeVibe';

const PAGE_SIZE = 25;

const INVALID_REASON_LABELS = {
  wrong_experience: 'Wrong world experience',
  unsupported_flask: 'Unsupported runner flask',
  production_identity_ineligible: 'Production identity was not eligible',
  practice_flask_in_production: 'Practice flask used in production',
  required_quest_already_completed: 'Required quest was already completed',
  character_already_attempted: 'Character already attempted this ruleset',
  debug_or_admin_shortcut: 'Debug or admin shortcut detected',
  ruleset_changed: 'Ruleset changed during the run',
  active_run_restarted: 'Active run was restarted',
  required_quest_chain_incomplete: 'Required quest chain was incomplete',
  character_persistence_failed: 'Character save was not confirmed',
  admin_invalidated: 'Invalidated after review',
} satisfies Record<TimedRunInvalidReason, string>;

function invalidReasonLabel(reason: TimedRunInvalidReason | undefined): string {
  return reason ? INVALID_REASON_LABELS[reason] : 'Reason unavailable';
}

function entryDetail(entry: TimedRunPublicEntry): string {
  if (entry.status === 'invalidated') {
    return `Invalidated · ${invalidReasonLabel(entry.invalidReason)}`;
  }
  if (entry.mode === 'practice') {
    return entry.identitySource === 'guest'
      ? 'Guest practice · excluded from ranked records'
      : 'Practice · excluded from ranked records';
  }
  return 'Ranked · production eligible';
}

interface RulesetChoice {
  rulesetId: string;
  releaseVersion: string;
  label: string;
  completionCount?: number;
}

async function fetchJson<T>(url: string, signal: AbortSignal): Promise<T> {
  const response = await fetch(url, { signal });
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(body.error ?? `Request failed (${response.status})`);
  }
  return response.json() as Promise<T>;
}

export const SpeedrunRecordsContent: React.FC = () => {
  const playerRun = useTimedRunClientState();
  const tokens = useHudTomeVibe(null);
  const [history, setHistory] = useState<TimedRunRulesetHistoryResponse | null>(null);
  const [selectedRuleset, setSelectedRuleset] = useState('');
  const [page, setPage] = useState(1);
  const [board, setBoard] = useState<TimedRunLeaderboardPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [includePractice, setIncludePractice] = useState(false);
  const [includeInvalidated, setIncludeInvalidated] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setError(null);
    fetchJson<TimedRunRulesetHistoryResponse>(`${API_URL}/speedruns/rulesets`, controller.signal)
      .then(result => {
        setHistory(result);
        setSelectedRuleset(current => current || result.currentRuleset?.rulesetId || result.rulesets[0]?.rulesetId || '');
      })
      .catch(reason => {
        if (reason instanceof DOMException && reason.name === 'AbortError') return;
        setError(reason instanceof Error ? reason.message : 'Could not load ruleset history.');
        setLoading(false);
      });
    return () => controller.abort();
  }, [refreshKey]);

  useEffect(() => {
    if (!selectedRuleset) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    const query = new URLSearchParams({
      ruleset: selectedRuleset,
      page: String(page),
      limit: String(PAGE_SIZE),
      includePractice: String(includePractice),
      includeInvalidated: String(includeInvalidated),
    });
    fetchJson<TimedRunLeaderboardPage>(
      `${API_URL}/speedruns/leaderboard?${query.toString()}`,
      controller.signal,
    )
      .then(result => setBoard(result))
      .catch(reason => {
        if (reason instanceof DOMException && reason.name === 'AbortError') return;
        setError(reason instanceof Error ? reason.message : 'Could not load the leaderboard.');
        setBoard(null);
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [selectedRuleset, page, refreshKey, includePractice, includeInvalidated]);

  const rulesets = useMemo<RulesetChoice[]>(() => {
    if (!history) return [];
    const choices: RulesetChoice[] = [];
    if (history.currentRuleset) {
      choices.push({
        rulesetId: history.currentRuleset.rulesetId,
        releaseVersion: history.currentRuleset.releaseVersion,
        label: `${history.currentRuleset.displayName} — current`,
      });
    }
    for (const row of history.rulesets) {
      if (choices.some(choice => choice.rulesetId === row.rulesetId)) {
        const current = choices.find(choice => choice.rulesetId === row.rulesetId)!;
        current.completionCount = row.completionCount;
        continue;
      }
      choices.push({
        rulesetId: row.rulesetId,
        releaseVersion: row.releaseVersion,
        label: `Historic ruleset ${row.rulesetId}`,
        completionCount: row.completionCount,
      });
    }
    return choices;
  }, [history]);

  const ownRun = playerRun.message?.run;
  const totalPages = Math.max(1, Math.ceil((board?.total ?? 0) / PAGE_SIZE));

  return (
    <div
      className={tokens.className}
      style={{
        ...tokens.style,
        minHeight: '100%',
        padding: 16,
        color: 'var(--tome-ink)',
        background: 'linear-gradient(180deg, var(--tome-cover-hi), var(--tome-cover-lo))',
        fontFamily: FONTS.ui,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <div>
          <div style={{ color: 'var(--tome-gilt-bright)', fontFamily: FONTS.display, fontSize: 20, textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}>Relight the Coil</div>
          <div style={{ marginTop: 3, color: 'var(--tome-paper-mid)', fontSize: 11, fontStyle: 'italic' }}>
            Full main story · timer ends on final quest turn-in
          </div>
        </div>
        <button
          type="button"
          onClick={() => setRefreshKey(value => value + 1)}
          style={{
            border: '1px solid var(--tome-rule-strong)',
            background: 'var(--tome-paper-hi)',
            color: 'var(--tome-ink)',
            borderRadius: 'var(--tome-entry-corner)',
            padding: '5px 12px',
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
          }}
        >
          Refresh
        </button>
      </div>

      {ownRun && (
        <div style={{
          marginTop: 14,
          padding: '10px 14px',
          border: '1px solid var(--tome-gilt)',
          borderRadius: 'var(--tome-leaf-corner)',
          background: 'linear-gradient(170deg, var(--tome-paper-hi), var(--tome-paper-mid) 55%, var(--tome-paper-lo))',
          boxShadow: 'var(--tome-shadow-leaf)',
          color: 'var(--tome-ink)',
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
        }}>
          <div>
            <div style={{ color: 'var(--tome-ochre)', fontSize: 9, letterSpacing: '0.12em', fontWeight: 700 }}>YOUR RUN</div>
            <div style={{ marginTop: 3, fontSize: 12, fontWeight: 600 }}>
              {ownRun.mode === 'ranked' ? 'Ranked' : 'Practice'} · {ownRun.status.replace(/_/g, ' ')}
              {ownRun.rank != null ? ` · #${ownRun.rank}${ownRun.boardTotal != null ? ` of ${ownRun.boardTotal}` : ''}` : ''}
            </div>
            {ownRun.runnerDisplayName && (
              <div style={{ marginTop: 3, color: 'var(--tome-moss)', fontSize: 10, fontWeight: 500 }}>
                Twitch · {ownRun.runnerDisplayName}
              </div>
            )}
            {ownRun.status === 'invalidated' && (
              <div style={{ marginTop: 3, color: 'var(--tome-danger)', fontSize: 10, fontWeight: 600 }}>
                {invalidReasonLabel(ownRun.invalidReason)}
              </div>
            )}
          </div>
          <div style={{ fontFamily: FONTS.mono, fontSize: 20, fontVariantNumeric: 'tabular-nums', color: 'var(--tome-ochre)', fontWeight: 700 }}>
            {formatTimedRunElapsed(timedRunElapsedMs(playerRun))}
          </div>
        </div>
      )}

      <div style={{ marginTop: 14, display: 'flex', alignItems: 'end', gap: 10 }}>
        <label style={{ flex: 1, color: 'var(--tome-paper-mid)', fontSize: 10, letterSpacing: '0.08em', fontWeight: 600 }}>
          VERSION &amp; RULESET
          <select
            aria-label="Version and ruleset"
            value={selectedRuleset}
            onChange={event => { setSelectedRuleset(event.target.value); setPage(1); }}
            style={{
              display: 'block',
              width: '100%',
              marginTop: 5,
              padding: '7px 10px',
              border: '1px solid var(--tome-rule-strong)',
              borderRadius: 'var(--tome-entry-corner)',
              background: 'var(--tome-paper-hi)',
              color: 'var(--tome-ink)',
              fontSize: 12,
            }}
          >
            {rulesets.length === 0 && <option value="">No published rulesets</option>}
            {rulesets.map(choice => (
              <option key={choice.rulesetId} value={choice.rulesetId}>
                {choice.label} · v{choice.releaseVersion}{choice.completionCount !== undefined ? ` · ${choice.completionCount} finishes` : ''}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div
        role="group"
        aria-label="Record visibility"
        style={{ marginTop: 9, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 18px', color: 'var(--tome-paper-mid)', fontSize: 10 }}
      >
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={includePractice}
            onChange={event => { setIncludePractice(event.target.checked); setPage(1); }}
          />
          Include guest / practice runs
        </label>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={includeInvalidated}
            onChange={event => { setIncludeInvalidated(event.target.checked); setPage(1); }}
          />
          Include invalidated runs
        </label>
        <span style={{ color: 'var(--tome-paper-lo)', fontStyle: 'italic' }}>Ranked production finishes only by default.</span>
      </div>

      {error && (
        <div role="alert" style={{ marginTop: 16, padding: 12, color: 'var(--tome-danger)', background: 'var(--tome-paper-hi)', border: '1px solid var(--tome-danger)', borderRadius: 'var(--tome-entry-corner)' }}>
          {error}
        </div>
      )}

      {!error && (
        <div style={{
          marginTop: 14,
          border: '1px solid var(--tome-rule-strong)',
          borderRadius: 'var(--tome-leaf-corner)',
          overflow: 'hidden',
          background: 'linear-gradient(170deg, var(--tome-paper-hi), var(--tome-paper-mid) 55%, var(--tome-paper-lo))',
          boxShadow: 'var(--tome-shadow-leaf)',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead style={{ background: 'var(--tome-paper-lo)', color: 'var(--tome-ink-muted)', textAlign: 'left', borderBottom: '1px solid var(--tome-rule-strong)' }}>
              <tr>
                <th style={{ padding: '8px 10px', width: 44 }}>#</th>
                <th style={{ padding: '8px 10px' }}>Runner</th>
                <th style={{ padding: '8px 10px' }}>Calling</th>
                <th style={{ padding: '8px 10px', textAlign: 'right' }}>Time</th>
                <th style={{ padding: '8px 10px', textAlign: 'right' }}>Version</th>
              </tr>
            </thead>
            <tbody>
              {board?.entries.map(entry => (
                <tr key={entry.runId} style={{ borderTop: '1px solid var(--tome-rule)' }}>
                  <td style={{ padding: '8px 10px', color: entry.rank != null && entry.rank <= 3 ? 'var(--tome-ochre)' : 'var(--tome-ink-muted)', fontWeight: entry.rank != null && entry.rank <= 3 ? 700 : 400 }}>
                    {entry.rank == null ? '—' : entry.mode === 'practice' ? `P${entry.rank}` : entry.rank}
                  </td>
                  <td style={{ padding: '8px 10px' }}>
                    <div style={{ fontWeight: entry.rank != null && entry.rank <= 3 ? 700 : 500, color: 'var(--tome-ink)' }}>{entry.heroName}</div>
                    {entry.runnerDisplayName && (
                      <div style={{ marginTop: 2, color: 'var(--tome-moss)', fontSize: 10 }}>
                        Twitch · {entry.runnerDisplayName}
                      </div>
                    )}
                    <div style={{ marginTop: 2, color: entry.status === 'invalidated' ? 'var(--tome-danger)' : 'var(--tome-ink-muted)', fontSize: 9 }}>
                      {entryDetail(entry)}
                    </div>
                  </td>
                  <td style={{ padding: '8px 10px', color: 'var(--tome-ink-soft)' }}>{formatCallingName(entry.callingId)}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: FONTS.mono, fontVariantNumeric: 'tabular-nums', color: 'var(--tome-ink)', fontWeight: 600 }}>
                    {entry.elapsedMs == null ? '—' : formatTimedRunElapsed(entry.elapsedMs)}
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', color: 'var(--tome-ink-muted)' }}>v{entry.releaseVersion}</td>
                </tr>
              ))}
              {!loading && (board?.entries.length ?? 0) === 0 && (
                <tr>
                  <td colSpan={5} style={{ padding: 24, textAlign: 'center', color: 'var(--tome-ink-muted)', fontStyle: 'italic' }}>
                    {includePractice || includeInvalidated
                      ? 'No runs match these visibility filters.'
                      : 'No production-eligible ranked finishes yet. Include test runs to inspect practice or invalidated attempts.'}
                  </td>
                </tr>
              )}
              {loading && (
                <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: 'var(--tome-ink-muted)' }}>Loading records…</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'var(--tome-paper-mid)', fontSize: 10 }}>
        <span>{board?.total ?? 0} {includePractice || includeInvalidated ? 'matching' : 'ranked'} runs</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage(value => Math.max(1, value - 1))}
            style={{
              background: 'var(--tome-paper-hi)',
              border: '1px solid var(--tome-rule-strong)',
              color: 'var(--tome-ink)',
              borderRadius: 'var(--tome-entry-corner)',
              padding: '3px 8px',
              fontSize: 10,
              cursor: page <= 1 ? 'default' : 'pointer',
              opacity: page <= 1 ? 0.5 : 1,
            }}
          >
            Previous
          </button>
          Page {page} / {totalPages}
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage(value => Math.min(totalPages, value + 1))}
            style={{
              background: 'var(--tome-paper-hi)',
              border: '1px solid var(--tome-rule-strong)',
              color: 'var(--tome-ink)',
              borderRadius: 'var(--tome-entry-corner)',
              padding: '3px 8px',
              fontSize: 10,
              cursor: page >= totalPages ? 'default' : 'pointer',
              opacity: page >= totalPages ? 0.5 : 1,
            }}
          >
            Next
          </button>
        </span>
      </div>
    </div>
  );
};
